import { format } from "date-fns";
import { forwardRef, type ComponentProps, type MouseEvent } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { RefreshCwIcon, XIcon } from "lucide-react";
import { DATE_SHORT } from "@/lib/date";

// v4 registry no longer exports ButtonProps — derive from the component.
type ButtonProps = ComponentProps<typeof Button>;

export const PickerDate = forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    date?: Date | null;
    setDate: (date: Date | null) => void;
    isLoading?: boolean;
  }
>(({ date, setDate, isLoading, ...props }, ref) => {
  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;
    setDate(null);
  };

  if (isLoading) {
    return (
      <Button
        variant="ghost"
        className="justify-start text-left font-normal pl-0"
        disabled
      >
        <RefreshCwIcon className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="ghost"
          className={cn(
            "justify-start text-left font-normal px-0",
            // Unset milestone dates are actionable debt — make them loud.
            !date && "text-destructive"
          )}
          {...props}
        >
          {date ? format(date, DATE_SHORT) : "Not set"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date ?? undefined}
          onSelect={(d) => setDate(d ?? null)}
        />
        {date && (
          <div className="flex justify-end border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-destructive hover:bg-destructive/10"
              onClick={handleClear}
            >
              <XIcon className="mr-1 h-3 w-3" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});

PickerDate.displayName = "PickerDate";
